import type { PropsWithChildren } from 'react'
import type { ItemSelectionProps } from './types'
import { PopoverBody, PopoverContent } from '@/scopes/common/components/Filters/Shared'
import * as Popover from '@radix-ui/react-popover'

import { tk, Typography } from '@sunwebgroup/ui-components'
import * as React from 'react'

import { css, styled } from 'styled-components'
import { useQuickSearchStore } from '../store'
import { useItemContext } from './context'

const TriggerButton = styled.button`
  border-radius: ${tk('radius_rounded')};
  height: 100%;
  padding: 0 ${tk('spacing_lg')};
  text-align: left;
  width: 100%;

  &::before {
    content: '';
    display: block;
    height: 40px;
    width: 1px;
    background-color: ${tk('color_semantic_border')};
    position: absolute;
    left: 0;
    top: 0;
    transform: translateY(50%);
  }

  &:disabled {
    background-color: ${tk('color_semantic_bg_disabled')};
  }
`

const Wrapper = styled.div<{ $isActive: boolean, $hasActiveSibling: boolean }>`
  cursor: pointer;
  margin-left: -1px;
  max-width: 200px;
  position: relative;
  @media ${tk('mq_desktop_md')} {
    max-width: 250px;
  }

  ${({ $isActive, $hasActiveSibling }) => !$isActive && !$hasActiveSibling && hoverMod()}
  ${({ $isActive, $hasActiveSibling }) => !$isActive && $hasActiveSibling && activeSiblingHoverMod()}
  ${({ $isActive }) => $isActive && isActiveMod()}
  &:first-child ${TriggerButton} {
    padding-left: ${tk('spacing_xl')};
  }

  &:last-child ${TriggerButton} {
    padding-right: ${tk('spacing_xl')};
  }
`

const SelectionTextWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`

const SelectionText = styled(Typography)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const PlaceholderText = styled(SelectionText)`
  color: ${tk('color_semantic_text_subdued')};
`

export function PillItem({
  additionalSelectedItems,
  selectionFormattedText,
  alignment = 'center',
  avoidCollisions = false,
  children,
  disabled,
  ...rest
}: PropsWithChildren<ItemSelectionProps>) {
  const { id, title, placeholder, onClose } = useItemContext()
  const { activeItemId, setActiveItem } = useQuickSearchStore()

  const isActive = activeItemId === id
  const hasActiveSibling = activeItemId !== null && !isActive

  const toggleActiveState = () => {
    if (isActive) {
      setActiveItem(null)
      onClose?.()
      return
    }
    setActiveItem(id)
  }

  return (
    <Popover.Root open={isActive} onOpenChange={toggleActiveState}>
      <Popover.Trigger asChild>
        <Wrapper className="pill-item" $isActive={isActive} $hasActiveSibling={hasActiveSibling} {...rest}>
          <TriggerButton disabled={disabled} tabIndex={0} onClick={toggleActiveState}>
            <Typography $variant="body-md-bold" as="p">
              {title}
            </Typography>
            <SelectionTextWrapper>
              {selectionFormattedText
                ? (
                    <SelectionText $variant="body-md" as="p">
                      {selectionFormattedText}
                    </SelectionText>
                  )
                : (
                    <PlaceholderText $variant="body-md" as="p">
                      {placeholder}
                    </PlaceholderText>
                  )}
              <Typography $variant="body-md" as="p">
                {additionalSelectedItems && additionalSelectedItems > 0 && `+${additionalSelectedItems}`}
              </Typography>
            </SelectionTextWrapper>
          </TriggerButton>
        </Wrapper>
      </Popover.Trigger>
      <Popover.Portal>
        <PopoverContent sideOffset={8} align={alignment} avoidCollisions={avoidCollisions}>
          <PopoverBody>{children}</PopoverBody>
        </PopoverContent>
      </Popover.Portal>
    </Popover.Root>
  )
}

function hoverMod() {
  return css`
  &:hover {
    background-color: ${tk('color_semantic_bg')};
    z-index: 1;
  }

  &:hover + & > ${TriggerButton} {
    &::before {
      content: initial;
    }
  }

  &:hover > ${TriggerButton} {
    background-color: ${tk('color_semantic_bg_subdued_hover')};

    &::before {
      content: initial;
    }
  }
`
}

function activeSiblingHoverMod() {
  return css`
  &:hover {
    background-color: ${tk('color_semantic_bg_subdued_hover')};
    z-index: 1;
  }

  &:hover > ${TriggerButton} {
    background-color: ${tk('color_semantic_bg_strong')};

    &::before {
      content: initial;
    }
  }
`
}

function isActiveMod() {
  return css`
  background-color: ${tk('color_semantic_bg_subdued_hover')};
  z-index: 1;

  ${TriggerButton}::before {
    content: initial;
  }

  ${TriggerButton} {
    border-radius: ${tk('spacing_2xl')};
    background-color: ${tk('color_semantic_bg')};
    box-shadow: ${tk('shadow_md_bottom')};
  }

  &:after {
    display: none;
  }
`
}
